import { reactive } from 'vue'
import { getMentionIdsForMessage, waitIfKey } from '../helperMethods'
import store from './store'
import { setVideosession, getChat } from './chats'
import { getMyUserId, setUser, getMyUser } from './memberships'
import { MESSAGE_PAGE_SIZE } from '@/helpers/constants'
import moment from 'moment'
import {
	normalizeMediaItem,
	storage,
	denormalizeMediaItem,
} from '@/helpers/utils'
import { timedLogger } from '@/helpers/log'
import { importTable } from '@/store/database'

/* Glossary:
	- "Parent Message": Any original message referenced by child messages in a thread
	- "Child Message": All messages in a thread, other than the original parent message
*/

const messagesById = reactive({})
const messagesByBubbleId = reactive({})
const messagesByTaskId = reactive({})
const messagesByThreadId = reactive({})
const cursorsByBubbleId = reactive({})

export const messagesData = {
	messagesById,
	messagesByBubbleId,
	messagesByTaskId,
	messagesByThreadId,
	cursorsByBubbleId,
}

function sortMessageIds(id1, id2) {
	if (!Number.isInteger(id1)) return 1 // sending or failed
	if (!Number.isInteger(id2)) return -1 // sending or failed
	return id1 - id2
}

const normalizeReactions = (reactions, reactionsummary) => {
	if (reactions && Object.keys(reactions).length > 0) return { reactions }
	if (!reactionsummary || !reactionsummary.length) return {}
	const myId = getMyUserId()
	return {
		reactions: reactionsummary.reduce(
			(acc, { users, count, emoji, emoji_base }) => {
				const me = users.includes(myId)
				acc[emoji] = { users, me, count, emoji, emoji_base }
				return acc
			},
			{}
		),
	}
}

const normalizeReplies = (parentmessage, message) => {
	setMessage(parentmessage)
	const threadId = getThreadIdFromMessage(message)
	const key = message.id || message.uuid
	if (threadId !== key) {
		messagesByThreadId[threadId] = {
			...(messagesByThreadId[threadId] || {}),
			[key]: key,
		}
	}
	return {
		threadId,
		isParent: isParentMessage(message),
	}
}

export const normalizeMedia = ({ messagemedia = [], attachments = [] }) => {
	const items = attachments?.length
		? attachments
		: messagemedia?.length
		? messagemedia
		: []
	const mediaCount = items.length || 0
	const normalized = items.map(normalizeMediaItem).sort((a, b) => {
		if (a.id && b.id) return a.id - b.id
		return 0
	})
	return {
		hasMedia: mediaCount > 0,
		mediaCount,
		media: normalized[0],
		attachments: normalized,
		messagemedia: normalized,
	}
}

const normalizeTranslation = (message) => {
	let { id, messagetrans, user_edited_version } = message
	const previous = getMessage(id)
	const isTranslated = message.isTranslated ?? previous?.isTranslated ?? false
	if (!messagetrans && previous?.messagetrans) {
		if (previous.user_edited_version === user_edited_version) {
			// messagetrans is not included in MessageUpdated events so
			// we need to persist it if the previous version had one and
			// assuming the user_edited_version hasn't changed
			messagetrans = previous.messagetrans
		}
	}
	if (Array.isArray(messagetrans)) {
		// if there are multiple translations, we'll need to find the one
		// in the list that matches our preferred language
		const me = getMyUser()
		messagetrans = messagetrans.find((mt) => mt.lang === me.language)
	}
	return { isTranslated, messagetrans }
}

const normalizeSendState = (message) => {
	const sendState = message.sendState || 'sent'
	return {
		sendState,
		isWaiting: sendState === 'waiting',
		isSending: sendState === 'sending',
		isFailed: sendState === 'failed',
		isSent: sendState === 'sent',
	}
}

export const normalizeMessage = (message) => {
	if (!message) return null
	const {
		user,
		task_id,
		reactions,
		reactionsummary = [],
		parent,
		parentmessage,
		meeting,
		videosession,
		...rest
	} = message
	const chat_id = rest.chat_id || rest.bubble_id
	setVideosession(meeting || videosession)
	setUser(user)
	const parentMsg = parent || parentmessage
	setMessage(parentMsg)
	const parent_id = rest.parent_id || rest.parentmessage_id // TODO: remove this once the migration is complete
	const first_child_id = rest.first_child_id || rest.firstchildmessage_id // TODO: remove this once the migration is complete
	const last_child_id = rest.last_child_id || rest.lastchildmessage_id // TODO: remove this once the migration is complete
	const system_event = rest.system_event || rest.systemevent // TODO: remove this once the migration is complete
	const text = rest.text || rest.message || '' // TODO: remove this once the migration is complete
	const local_id = rest.local_id || rest.uuid
	const system_message_parts =
		rest.system_message_parts || rest.systemmessageparts // TODO: remove this once the migration is complete

	if (rest.mentions && rest.mentions.length > 0) {
		rest.mentions.forEach((mention) => {
			if (mention && typeof mention === 'object' && 'id' in mention) {
				setUser(mention)
			}
		})
	}

	return {
		...rest,
		key: local_id || rest.id,
		chat_id,
		bubble_id: chat_id, // TODO: remove this once the migration is complete
		task_id: task_id || null,
		message: rest.message || '',
		isEdited: !!rest.user_edited_at,
		meeting_id: rest.meeting_id || rest.videosession_id,
		videosession_id: rest.meeting_id || rest.videosession_id, // TODO: remove this once the migration is complete
		parent_id,
		parentmessage_id: parent_id, // TODO: remove this once the migration is complete
		first_child_id,
		firstchildmessage_id: first_child_id, // TODO: remove this once the migration is complete
		last_child_id,
		lastchildmessage_id: last_child_id, // TODO: remove this once the migration is complete
		system_event,
		systemevent: system_event, // TODO: remove this once the migration is complete
		text,
		message: text, // TODO: remove this once the migration is complete
		local_id,
		uuid: local_id, // TODO: remove this once the migration is complete
		system_message_parts,
		systemmessageparts: system_message_parts, // TODO: remove this once the migration is complete
		mentions: getMentionIdsForMessage(rest),
		...normalizeSendState(rest),
		...normalizeReactions(reactions, reactionsummary),
		...normalizeReplies(parentMsg, rest),
		...normalizeMedia(rest),
		...normalizeTranslation(rest),
	}
}

const customFields = [
	'hasMedia',
	'isEdited',
	'isFailed',
	'isSending',
	'isParent',
	'isSending',
	'isSent',
	'isTranslated',
	'media',
	'sendState',
	'threadId',
]
export const denormalizeMessage = (message) => {
	message = Object.assign({}, message)
	customFields.forEach((key) => {
		delete message[key]
	})
	if (!message.task_id) {
		delete message.task_id
	}
	if (message.messagemedia?.length) {
		message.messagemedia = message.messagemedia.map(denormalizeMediaItem)
	}
	return message
}

const alertAboutChanges = (messages = []) => {
	// TODO: figure out a better way to alert Chat about changes
	// that doesn't involve EventBus
	EventBus.$emitDebounced(
		'message-change',
		messages.filter((m) => m).map((m) => m.id)
	)
}

const isOldVersion = (message) => {
	if (!message) return null
	const previous = getMessage(message.id)
	if (previous) {
		return moment(previous.updated_at).isAfter(message.updated_at)
	}
	return false
}

export const setMessage = (message, skipDB = false) => {
	if (!message || isOldVersion(message)) return null
	if (message.id) deleteMessage(message.uuid)
	message = normalizeMessage(message)
	const key = message.id || message.uuid
	if (message.bubble_id) {
		messagesByBubbleId[message.bubble_id] = {
			...(messagesByBubbleId[message.bubble_id] || {}),
			[key]: key,
		}
	}
	if (message.task_id) {
		messagesByTaskId[message.task_id] = {
			...(messagesByTaskId[message.task_id] || {}),
			[key]: key,
		}
	}
	messagesById[key] = message
	if (!skipDB) {
		if (message.id) {
			store.dbPut('messages', message)
		}
		alertAboutChanges([message])
	}
	return message
}

export const setMessages = (messages, skipDB) => {
	if (!messages) return null
	const normal = messages.map((msg) => setMessage(msg, true)).filter((m) => m)
	if (normal.length && !skipDB) {
		store.dbBulkPut('messages', normal)
		alertAboutChanges(normal)
	}
	return normal
}

export const setMedia = (media) => {
	if (!media || (!media.messageUUID && !media.message_id)) return null
	const msgId = media.messageUUID || media.message_id
	const message = getMessage(msgId)
	if (!message || !message.messagemedia?.length) return null
	media = normalizeMediaItem(media)
	const existingMedia = message.attachments || message.messagemedia // TODO: remove this once the migration is complete
	const updatedMedia = existingMedia.map((m) => {
		if (m.tempId === media.tempId || m.uuid === media.uuid) {
			return media
		}
		return m
	})
	message.attachments = updatedMedia
	message.messagemedia = updatedMedia // TODO: remove this once the migration is complete
	setMessage(message)
	return media
}

export const diffAndDeleteMessagesOutsideRange = (messages) => {
	if (!messages || !messages.length) return null
	const { bubble_id } = messages[0]
	const log = timedLogger(`diffing messages for chat ${bubble_id}`)
	log('starting')
	// collect all of the message ids in this set
	const newMessageIds = messages.map((m) => m.id)
	// as well as the parent ids for any messages in this set
	const parentIds = new Set(
		messages.map((m) => m.parentmessage_id).filter((x) => x)
	)
	const oldestIdInSet = Math.min(...newMessageIds)
	const newestIdInSet = Math.max(...newMessageIds)
	const pinnedMessageId = getChat(bubble_id).pinned_message_id
	// find any messages in the cache that aren't in these new sets
	// and delete them since they were likely deleted/archived on server

	const idsToDelete = getMessageIdsForBubble(bubble_id).filter((id) => {
		const hasId = Number.isInteger(id)
		const isInNewSet = newMessageIds.includes(id)
		const isParent = parentIds.has(id)
		const isOlderThanOldestInSet = id < oldestIdInSet
		const isWithinRange = id <= newestIdInSet && id >= oldestIdInSet
		const isPinned = id === pinnedMessageId
		if (!hasId) return false // keep all messages that have no id (only uuid)
		if (isInNewSet) return false // message is in server set, so its still valid
		if (isParent) return false // message is a parent of message in server set, so keep it
		if (isPinned) return false // message is pinned for group so keep it
		if (isOlderThanOldestInSet) return true // message is old, needs to be trimmed/deleted
		if (isWithinRange) return true // message is not in set and is within range, so it was deleted
		return false // message falls outside of range so we can't
	})

	log('deleting ids...', idsToDelete)
	deleteMessages(idsToDelete)
	log('done deleting')
	log.print()
}

export const deleteCachedMessagesNotInSet = (messages) => {
	if (!messages || !messages.length) return null
	const { bubble_id } = messages[0]
	const log = timedLogger(`trimming messages cache for chat ${bubble_id}`)
	log('starting')
	// collect all of the message ids in this set
	const newMessageIds = messages.map((m) => m.id)
	// as well as the parent ids for any messages in this set
	const parentIds = new Set(
		messages.map((m) => m.parentmessage_id).filter((x) => x)
	)
	const pinnedMessageId = getChat(bubble_id).pinned_message_id

	const idsToDelete = getMessageIdsForBubble(bubble_id).filter((id) => {
		const hasId = Number.isInteger(id)
		const isInNewSet = newMessageIds.includes(id)
		const isParent = parentIds.has(id)
		const isPinned = id === pinnedMessageId
		if (!hasId) return false // keep all messages that have no id (only uuid)
		if (isInNewSet) return false // message is in server set, so its still valid
		if (isParent) return false // message is a parent of message in server set, so keep it
		if (isPinned) return false // message is pinned for group so keep it
		return true // message does not existing in this set, so trim it
	})
	log('deleting ids...', idsToDelete)
	deleteMessages(idsToDelete)
	log('done deleting')
	log.print()
}

export const getMessage = (messageId) => {
	return messagesById[messageId] || null
}

export const getMessageIdsForBubble = (bubbleId) => {
	// key is a string, value is an int, so we want Object.values() here
	return Object.values(messagesByBubbleId[bubbleId] || {})
}

export const getSortedMessageIdsForBubble = (bubbleId) => {
	return getMessageIdsForBubble(bubbleId).sort(sortMessageIds)
}

export const getSortedMessageIds = () => {
	return getSortedMessageIdsForBubble(store.currentChatId)
}

export const getMessagesForBubble = (bubbleId) => {
	return getSortedMessageIdsForBubble(bubbleId).map(getMessage)
}

export const getMessageIds = () => {
	return getSortedMessageIdsForBubble(store.currentChatId)
}

export const getMessages = () => {
	const id = store.currentChatId
	const { start, stop } = getCursorsForBubble(id)
	return getMessagesForBubble(id).filter(
		(m) => !m.id || (m.id >= start && m.id <= stop)
	)
}

export const getNonSystemMessages = () => {
	return getMessages().filter((m) => m.systemevent === null)
}

export const getMostRecentMessageForBubble = (bubbleId) => {
	const [mostRecentId] = getSortedMessageIdsForBubble(bubbleId).slice(-1)
	return getMessage(mostRecentId)
}

export const getOldestMessageForBubble = (bubbleId) => {
	const [oldestId] = getSortedMessageIdsForBubble(bubbleId)
	return getMessage(oldestId)
}

export const getMostRecentMessage = () => {
	return getMostRecentMessageForBubble(store.currentChatId)
}

export const getOldestMessage = () => {
	return getOldestMessageForBubble(store.currentChatId)
}

export const getMessageIdsForTaskId = (taskId) => {
	return Object.values(messagesByTaskId[taskId] || {})
}

export const updateMessage = (id, dataOrUpdater = {}) => {
	if (!id) return null
	const message = getMessage(id) || { id }
	if (typeof dataOrUpdater === 'function') {
		return setMessage(dataOrUpdater(message))
	}
	return setMessage({ ...message, ...dataOrUpdater })
}

export const deleteMessage = (id, skipAlert) => {
	const message = getMessage(id)
	if (!message) return null
	delete messagesById[id]
	store.dbDeleteByKey('messages', id)
	if (message.bubble_id) {
		delete messagesByBubbleId[message.bubble_id][id]
	}
	if (messagesByThreadId[message.threadId]) {
		delete messagesByThreadId[message.threadId][id]
	}
	if (!skipAlert) {
		alertAboutChanges([message])
	}
}

export const deleteMessages = (ids) => {
	ids.forEach((id) => deleteMessage(id, true))
	alertAboutChanges([])
}

export const deleteOldestMessagesForBubble = (bubbleId) => {
	const messages = getMessagesForBubble(bubbleId)
	if (messages.length <= MESSAGE_PAGE_SIZE) return
	// messages are in reverse order, so we have to delete from the bottom
	let cursorIndex = messages.length - MESSAGE_PAGE_SIZE
	if (cursorIndex < 0) {
		cursorIndex = 0
	}
	const cursor = messages[cursorIndex].id
	const pinnedMessageId = getChat(bubbleId).pinned_message_id
	setStartCursorForBubble(bubbleId, cursor)
	const idsToDelete = messages
		.slice(0, cursorIndex)
		.filter((m) => !m.isParent && m.id !== pinnedMessageId)
		.map((m) => m.id)
	deleteMessages(idsToDelete)
}

export const importMessagesFromCacheForBubble = async (
	bubble_id,
	skipCursors
) => {
	if (isNaN(parseInt(bubble_id))) {
		log(`importMessagesFromCacheForBubble function passed bad ID ${bubble_id}`)
		return
	}
	let isHistoryComplete = false
	const onHistoryComplete = (bubbleId) => {
		// if bubble.history finishes first, then don't update from the cache
		if (bubble_id === bubbleId) {
			isHistoryComplete = true
		}
	}
	EventBus.$on('bubbleHistoryComplete', onHistoryComplete)
	// leaving this logging in here at debug level so we can diagnose
	//  any future issues that might arise with slow db performance
	const log = timedLogger(`messages cache for chat ${bubble_id}`)
	log('starting query')
	try {
		const messages = await importTable('messages', { where: { bubble_id } })
		await waitIfKey('slowDB', 8000)
		log('query complete')
		if (isHistoryComplete) {
			log('bubble.history finished first')
			return
		}
		log('updating store...')
		const normalized = setMessages(messages, true)
		log('store updated')
		if (!skipCursors) {
			const sortedIds = normalized.map((m) => m.id).sort(sortMessageIds)
			const [startId] = sortedIds
			const [stopId] = sortedIds.slice(-1)
			setCursorsForBubble(bubble_id, { start: startId, stop: stopId })
			log('cursors updated')
		}
		alertAboutChanges(normalized)
	} catch (error) {
		console.error('Cannot decrypt messages', error)
	}
	log.print()
	EventBus.$off('bubbleHistoryComplete', onHistoryComplete)
}

const resolveMessageOrId = (messageOrId) => {
	return messageOrId.uuid || messageOrId.id
		? messageOrId
		: getMessage(messageOrId)
}

export const isParentMessage = (messageOrId) => {
	const message = resolveMessageOrId(messageOrId)
	if (!message) return null
	return !!message.firstchildmessage_id
}

export const getThreadIdFromMessage = (messageOrId) => {
	const message = resolveMessageOrId(messageOrId)
	if (!message) return null
	const { threadId, parentmessage_id, id, uuid } = message
	if (threadId) return threadId
	if (parentmessage_id) return parentmessage_id
	return id || uuid
}

export const getChildMessageIdsInThread = (threadId) => {
	// this will not include the parent/original message, only children
	return Object.values(messagesByThreadId[threadId] || {})
}

export const getChildMessagesInThread = (threadId) => {
	return getChildMessageIdsInThread(threadId)
		.sort(sortMessageIds)
		.map(getMessage)
}

export const getAllMessagesInThread = (threadId) => {
	if (!threadId) return []
	return [getMessage(threadId)].concat(getChildMessagesInThread(threadId))
}

export const getThreadForMessage = (messageOrId) => {
	const threadId = getThreadIdFromMessage(messageOrId)
	if (!threadId) return null
	return getAllMessagesInThread(threadId)
}

export const getCursorsForBubble = (bubbleId) => {
	return cursorsByBubbleId[bubbleId] || {}
}

export const getStartCursorForBubble = (bubbleId) => {
	return getCursorsForBubble(bubbleId).start
}

export const getStopCursorForBubble = (bubbleId) => {
	return getCursorsForBubble(bubbleId).stop
}

export const setCursorsForBubble = (bubbleId, cursors) => {
	cursorsByBubbleId[bubbleId] = cursors
	storage.set('cursorByBubbleId', cursorsByBubbleId)
	return cursors
}

export const setStartCursorForBubble = (bubbleId, start) => {
	const cursors = getCursorsForBubble(bubbleId)
	cursors.start = start
	return setCursorsForBubble(bubbleId, cursors)
}

export const setStopCursorForBubble = (bubbleId, stop) => {
	const cursors = getCursorsForBubble(bubbleId)
	cursors.stop = stop
	if (!cursors.start) {
		setStartCursorForBubble(bubbleId, stop)
	}
	return setCursorsForBubble(bubbleId, cursors)
}

export const importCursors = () => {
	try {
		const cached = storage.get('cursorByBubbleId')
		Object.assign(cursorsByBubbleId, cached ? cached : {})
	} catch (e) {
		console.error(e)
	}
}

export const setMessageAndMoveCursor = (message) => {
	const ids = getMessageIdsForBubble(message.bubble_id).filter(Number.isInteger)
	ids.push(message.id)
	const id = ids.sort(sortMessageIds).slice(-1)[0]
	setStopCursorForBubble(message.bubble_id, id)
	setMessage(message)
}
